import { useCallback, useMemo } from 'react';

import includes from 'lodash/includes';
import orderBy from 'lodash/orderBy';
import partition from 'lodash/partition';
import range from 'lodash/range';
import sumBy from 'lodash/sumBy';

import { useTracking } from '@change-corgi/core/react/tracking';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useTriggerSharePetitionModal } from 'src/app/pages/starterDashboardV2/shared/hooks';

import { CHANNEL_SETTINGS, OTHER_CHANNEL_KEY } from '../../constants';
import type { ChannelSignatureData } from '../../types';

type UseTopSignatureSourcesResult = ModelHookResult<
	{
		channelSignatureCounts: ChannelSignatureData[];
		topChannel?: ChannelSignatureData;
		otherSignatureSource?: {
			signaturePercent: number;
		};
		showInsightBox: boolean;
		promoteLink: string;
	},
	{
		handleShareClick: () => Promise<void>;
		handlePromoteClick: () => void;
	}
>;

export function useTopSignatureSources(): UseTopSignatureSourcesResult {
	const { data } = useStarterDashboardV2PageData();
	const track = useTracking();
	const triggerSharePetitionModal = useTriggerSharePetitionModal();

	const promoteLink = `/p/${encodeURIComponent(data.petition.slug)}/sponsors/new?source_location=petition_manage_sponsor_ad`;

	const channelSignatureCounts = useMemo(() => {
		if (data.petition.signatureCountsByChannel.__typename !== 'SignatureCountsByChannel') return [];

		const { totalCount, countsByChannel } = data.petition.signatureCountsByChannel;
		const channelList = calculateChannelPercentages(consolidateChannels(countsByChannel), totalCount).map(
			({ channel, signaturePercent, signatureCountLocalizedCompact, signatureCount }) => {
				const { iconColor, translationKey, icon, isLegacyIcon } = CHANNEL_SETTINGS[channel];
				return {
					channel,
					icon,
					iconColor,
					signatureCount,
					isLegacyIcon,
					signatureCountLocalizedCompact,
					signaturePercent,
					translationKey,
				};
			},
		);

		return orderBy(channelList, ['signaturePercent', 'channel'], ['desc', 'asc']);
	}, [data.petition.signatureCountsByChannel]);

	const [otherChannel, restOfChannels] = partition(channelSignatureCounts, ['channel', 'other']);

	const topChannel = restOfChannels.at(0);
	const showInsightBox =
		topChannel !== undefined &&
		!includes(['organic_search', 'change_email', 'qr_code', 'linktree'], topChannel.channel);

	const otherSignatureSource =
		otherChannel.length === 1 ? { signaturePercent: otherChannel[0].signaturePercent } : undefined;

	const handleShareClick: () => Promise<void> = useCallback(async () => {
		void track('petition_manage_signature_insights_box_click', { topChannel: channelSignatureCounts[0].channel });
		await triggerSharePetitionModal();
	}, [track, channelSignatureCounts, triggerSharePetitionModal]);

	const handlePromoteClick: () => void = useCallback(() => {
		void track('petition_manage_signature_insights_box_click', { topChannel: channelSignatureCounts[0].channel });
	}, [track, channelSignatureCounts]);

	return {
		data: {
			channelSignatureCounts: restOfChannels,
			topChannel,
			otherSignatureSource,
			promoteLink,
			showInsightBox,
		},
		actions: { handleShareClick, handlePromoteClick },
	};
}

type ChannelSignatureCounts = ReadonlyArray<{
	readonly channel: string;
	readonly count: number;
	readonly countLocalizedCompact: string;
}>;

/**
 * Consolidate channels missing translations into an 'other' channel.
 */
function consolidateChannels(countsByChannel: ChannelSignatureCounts) {
	const channelsMissingTranslations = countsByChannel.filter(
		({ channel }) => !Object.hasOwn(CHANNEL_SETTINGS, channel),
	);
	if (channelsMissingTranslations.length === 0) return countsByChannel;

	// Find the 'other' channel or create a new one
	const existingOtherChannel = countsByChannel.find(({ channel }) => channel === OTHER_CHANNEL_KEY);
	const otherChannel = existingOtherChannel
		? { ...existingOtherChannel }
		: {
				channel: OTHER_CHANNEL_KEY,
				count: 0,
				countLocalizedCompact: '0',
			};

	// Add counts from channels that are missing translations to the 'other' channel
	otherChannel.count += sumBy(channelsMissingTranslations, 'count');
	// CHANGE-66029: We won't worry about formatting this number because handling
	// of the other channel is changing
	otherChannel.countLocalizedCompact = otherChannel.count.toString();

	// Construct the final channel list
	const channelList = countsByChannel.filter(
		({ channel }) => Object.hasOwn(CHANNEL_SETTINGS, channel) && channel !== OTHER_CHANNEL_KEY,
	);
	if (otherChannel.count > 0) channelList.push(otherChannel);

	return channelList;
}

/**
 * Assign percentages to each channel based on the number of signatures they have.
 */
function calculateChannelPercentages(signatureCountsByChannel: ChannelSignatureCounts, totalCount: number) {
	if (totalCount === 0) return [];

	// Store percentage and remainder for each channel
	const preliminaryPercentages = signatureCountsByChannel.map(({ channel, count, countLocalizedCompact }) => ({
		channel,
		percentage: Math.floor((count / totalCount) * 100),
		remainder: ((count / totalCount) * 100) % 1, // Store the fractional part
		countLocalizedCompact,
		count,
	}));

	const totalPercentage = sumBy(preliminaryPercentages, 'percentage');
	const difference = 100 - totalPercentage;

	// Sort by remainder from highest to lowest and distribute the remaining percentage(s)
	const sortedByRemainder = orderBy(preliminaryPercentages, ['remainder', 'percentage'], ['desc', 'asc']);
	range(0, difference).forEach((index) => {
		sortedByRemainder[index].percentage += 1;
	});

	// Filter out channels with 0 percent
	const withNonZeroPercentage = sortedByRemainder.filter(({ percentage }) => percentage > 0);

	return withNonZeroPercentage.map(({ channel, percentage, countLocalizedCompact, count }) => ({
		channel,
		signaturePercent: percentage,
		signatureCountLocalizedCompact: countLocalizedCompact,
		signatureCount: count,
	}));
}
